"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/components/ui/cn";

export interface TabItem {
  href: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  className?: string;
}

/**
 * Segmented-control style navigation tabs backed by real routes (each tab is
 * a `<Link>`). Active tab is derived from whether the current pathname
 * starts with the tab's `href`, so nested routes keep their parent tab lit.
 */
export function Tabs({ items, className }: TabsProps) {
  const pathname = usePathname();

  return (
    <div
      id="tabs"
      role="tablist"
      className={cn(
        "inline-flex items-center gap-1 rounded-lg border border-border bg-surface-muted/60 p-1",
        className
      )}
    >
      {items.map((item) => {
        const active =
          pathname === item.href || pathname.startsWith(`${item.href}/`);
        return (
          <Link
            key={item.href}
            href={item.href}
            // No viewport prefetch: the workflow's pushState URL changes make
            // the router re-prefetch both routes, doubling RSC requests on a
            // phone connection for a tab that is rarely switched.
            prefetch={false}
            role="tab"
            aria-selected={active}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors",
              active
                ? "bg-surface text-foreground shadow-sm"
                : "text-muted hover:text-foreground"
            )}
          >
            {item.icon}
            {item.label}
          </Link>
        );
      })}
    </div>
  );
}
